import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';

const emptyForm = {
  title: '',
  idea: '',
  targetDate: '',
  progress: 0
};

export default function GoalsTracker() {
  const { user } = useAuth();
  const storageKey = useMemo(() => `shem-goals-${user?.id || 'guest'}`, [user?.id]);
  const [goals, setGoals] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [view, setView] = useState('active');

  useEffect(() => {
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setGoals(parsed);
      } catch (err) {
        console.error('Failed to load goals', err);
      }
    }
  }, [storageKey]);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(goals));
  }, [goals, storageKey]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!form.title.trim()) return;
    if (editingId) {
      setGoals(prev => prev.map(goal => (goal.id === editingId ? { ...goal, ...form } : goal)));
      setEditingId(null);
    } else {
      setGoals(prev => [
        {
          id: `goal-${Date.now()}`,
          ...form,
          createdAt: new Date().toISOString()
        },
        ...prev
      ]);
    }
    setForm(emptyForm);
  };

  const handleEdit = (goal) => {
    setEditingId(goal.id);
    setForm({
      title: goal.title || '',
      idea: goal.idea || '',
      targetDate: goal.targetDate || '',
      progress: typeof goal.progress === 'number' ? goal.progress : 0
    });
  };

  const handleDelete = (goalId) => {
    if (!window.confirm('Remove this goal?')) return;
    setGoals(prev => prev.filter(goal => goal.id !== goalId));
    if (editingId === goalId) {
      setEditingId(null);
      setForm(emptyForm);
    }
  };

  const handleProgressChange = (goalId, value) => {
    setGoals(prev =>
      prev.map(goal => (goal.id === goalId ? { ...goal, progress: value } : goal))
    );
  };
  const visibleGoals = goals.filter(goal => view === 'all' || (view === 'completed' ? Number(goal.progress) >= 100 : Number(goal.progress) < 100));
  const toggleComplete = goal => handleProgressChange(goal.id, Number(goal.progress) >= 100 ? 0 : 100);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Goals</h1>
        <p className="page-subtitle">Set and manage your personal academic goals.</p>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <h3 style={{ fontSize: '1.125rem' }}>{editingId ? 'Update Goal' : 'Add a Goal'}</h3>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '12px' }}>
            <label className="form-label">Goal title<input
              className="form-input"
              value={form.title}
              onChange={(event) => setForm(prev => ({ ...prev, title: event.target.value }))}
            /></label>
            <label className="form-label">Description / steps<textarea
              className="form-input form-textarea"
              value={form.idea}
              onChange={(event) => setForm(prev => ({ ...prev, idea: event.target.value }))}
              rows={4}
            /></label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
              <div>
                <label className="form-label" style={{ display: 'block', marginBottom: '6px' }}>
                  Target date
                </label>
                <input
                  className="form-input"
                  type="date"
                  value={form.targetDate}
                  onChange={(event) => setForm(prev => ({ ...prev, targetDate: event.target.value }))}
                  style={{ width: '100%', maxWidth: '260px' }}
                />
              </div>
              <div>
                <label className="form-label" style={{ display: 'block', marginBottom: '6px' }}>
                  Progress: {form.progress}%
                </label>
                <input
                  className="form-input"
                  type="range"
                  min="0"
                  max="100"
                  value={form.progress}
                  onChange={(event) => setForm(prev => ({ ...prev, progress: Number(event.target.value) }))}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button className="btn btn-primary" type="submit">
                {editingId ? 'Save changes' : 'Add goal'}
              </button>
              {editingId && (
                <button
                  className="btn btn-outline"
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    setForm(emptyForm);
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      <div className="lms-goal-tabs" role="tablist">{[['active', 'Active'], ['completed', 'Completed'], ['all', 'All']].map(([value, label]) => <button key={value} className={`btn ${view === value ? 'btn-primary' : 'btn-outline'}`} aria-pressed={view === value} onClick={() => setView(value)}>{label}</button>)}</div>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
        {visibleGoals.length === 0 && (
          <div className="card">
            <div className="card-body" style={{ color: 'var(--gray-500)', textAlign: 'center' }}>
              {view === 'completed' ? 'No completed goals yet.' : 'No active goals yet. Add a goal to get started.'}
            </div>
          </div>
        )}
        {visibleGoals.map(goal => (
          <div key={goal.id} className="card" style={{ display: 'grid', gap: '12px' }}>
            <div className="card-body" style={{ display: 'grid', gap: '10px' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{goal.title}</div>
                {goal.targetDate && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginTop: '4px' }}>
                    Target: {goal.targetDate}
                  </div>
                )}
              </div>
              {goal.idea && (
                <div style={{ fontSize: '0.875rem', color: 'var(--gray-700)', whiteSpace: 'pre-line' }}>
                  {goal.idea}
                </div>
              )}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: 'var(--gray-500)' }}>
                  <span>Progress</span>
                  <span>{goal.progress || 0}%</span>
                </div>
                <input
                  className="form-input"
                  type="range"
                  min="0"
                  max="100"
                  value={goal.progress || 0}
                  onChange={(event) => handleProgressChange(goal.id, Number(event.target.value))}
                />
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button className="btn btn-primary btn-sm" onClick={() => toggleComplete(goal)}>{Number(goal.progress) >= 100 ? 'Reopen Goal' : 'Mark Complete'}</button>
                <button className="btn btn-outline btn-sm" onClick={() => handleEdit(goal)}>
                  Edit
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(goal.id)}>
                  Remove
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
